import React, { useState, useEffect, useRef } from "react";

const ENV_MAPBOX_KEY = process.env.REACT_APP_MAPBOX_API_KEY;

const AddressAutocomplete = ({
  name,
  label,
  placeholder,
  value = "",
  onChange,
  onBlur,
  error,
  helperText,
  disabled = false,
  onAddressSelect,
  onAddressClear,
  apiKey,
  countryCode = "dk", // Make country configurable
  limit = 8,
  minSearchLength = 3,
  language = "da", // Default to Danish
  required = false,
  ...props
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const debounceTimer = useRef(null);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  apiKey = ENV_MAPBOX_KEY;

  // Debounce function
  const debounce = (func, delay) => {
    return (...args) => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      debounceTimer.current = setTimeout(() => func.apply(null, args), delay);
    };
  };

  // Clean address to remove non-Danish/English text and extra regional info
  const cleanAddressForDAWA = (displayName, addressDetails) => {
    // Split by commas
    const parts = displayName.split(",").map((part) => part.trim());

    // Filter out parts with non-Latin characters (like Cyrillic)
    const cleanParts = parts.filter((part) => {
      // Check if part contains only Latin characters, numbers, spaces, and common punctuation
      return /^[\w\s\d.,\-æøåÆØÅ]+$/.test(part);
    });

    // For Danish addresses, we want: Street, City, Postal Code, Country
    // DAWA needs actual street addresses, not building/landmark names
    const relevantParts = [];

    // Try to get actual street address from addressDetails first
    // Priority: road > street > house_number + road
    let streetAddress = null;

    if (addressDetails?.road) {
      streetAddress = addressDetails.road;
      // Add house number if available
      if (addressDetails?.house_number) {
        streetAddress = `${addressDetails.road} ${addressDetails.house_number}`;
      }
    } else if (addressDetails?.street) {
      streetAddress = addressDetails.street;
    }

    // If we have a proper street address, use it
    // Otherwise fall back to first clean part (might be building name)
    if (streetAddress) {
      relevantParts.push(streetAddress);
    } else if (cleanParts[0]) {
      relevantParts.push(cleanParts[0]);
    }

    // Add city if available
    const city = addressDetails?.city || addressDetails?.town || addressDetails?.village;
    if (city && !relevantParts.includes(city)) {
      relevantParts.push(city);
    }

    // Add postal code if available
    if (addressDetails?.postcode) {
      relevantParts.push(addressDetails.postcode);
    }

    // Add country only if it's Denmark/Danmark
    const country = addressDetails?.country;
    if (country && (country === "Denmark" || country === "Danmark")) {
      relevantParts.push(country);
    }

    return relevantParts.join(", ");
  };

  // Fetch addresses from LocationIQ API
  const fetchAddresses = async (query) => {
    if (!query || query.length < minSearchLength) {
      setOptions([]);
      setOpen(false);
      return;
    }

    setLoading(true);

    try {
      // Add accept-language header to get Danish/English results
      const acceptLanguage = language === "da" ? "da,en" : "en,da";
      const url = `https://us1.locationiq.com/v1/autocomplete?key=${apiKey}&q=${encodeURIComponent(query)}&limit=${limit}&format=json&countrycodes=${countryCode}&addressdetails=1&accept-language=${acceptLanguage}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          accept: "application/json",
          "Accept-Language": acceptLanguage,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (Array.isArray(data)) {
        const formattedOptions = data.map((item, index) => {
          // Clean the address for DAWA compatibility
          const cleanedAddress = cleanAddressForDAWA(item.display_name, item.address);

          // Extract street address for DAWA
          let streetAddress = null;
          if (item.address?.road) {
            streetAddress = item.address.road;
            if (item.address?.house_number) {
              streetAddress = `${item.address.road} ${item.address.house_number}`;
            }
          } else if (item.address?.street) {
            streetAddress = item.address.street;
          }

          return {
            id: item.place_id || index,
            label: cleanedAddress,
            address: cleanedAddress,
            streetAddress: streetAddress, // Actual street address for DAWA
            postcode: item.address?.postcode || "",
            city:
              item.address?.city ||
              item.address?.town ||
              item.address?.village ||
              item.address?.municipality ||
              item.address?.name ||
              "",
            country: item.address?.country || "",
            state: item.address?.state || "",
            type: item.type || item.class,
            lat: parseFloat(item.lat),
            lng: parseFloat(item.lon),
            place_id: item.place_id,
            osm_id: item.osm_id,
            osm_type: item.osm_type,
            display_place: item.display_place,
            display_address: item.display_address,
            boundingbox: item.boundingbox,
          };
        });

        setOptions(formattedOptions);
        setOpen(true);
        setHighlightedIndex(-1);
      } else {
        setOptions([]);
        setOpen(false);
      }
    } catch (error) {
      setOptions([]);
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  // Debounced version of fetchAddresses
  const debouncedFetchAddresses = debounce(fetchAddresses, 300);

  // Handle input change
  const handleInputChange = (event) => {
    const newValue = event.target.value;
    setInputValue(newValue);

    // If address is cleared, clear related fields
    if (!newValue || newValue.length < minSearchLength) {
      if (onAddressClear) {
        onAddressClear();
      }
    }

    debouncedFetchAddresses(newValue);

    // Call parent onChange for form handling
    if (onChange) {
      onChange(event);
    }
  };

  // Handle option selection
  const handleOptionSelect = (option) => {
    setInputValue(option.address);
    setOpen(false);
    setHighlightedIndex(-1);

    // Create synthetic event for form libraries like Formik
    // const syntheticEvent = {
    //   target: {
    //     name: name,
    //     value: option.address,
    //   },
    // };

    // if (onChange) {
    //   onChange(syntheticEvent);
    // }

    onAddressSelect({
      address: option.address,
      postcode: option.postcode,
      city: option.city,
      country: option.country,
      state: option.state,
      lat: option.lat,
      lng: option.lng, // Changed from 'lon' to 'lng'
      type: option.type,
      fullData: option,
    });
  };

  // Handle keyboard navigation
  const handleKeyDown = (event) => {
    if (!open || options.length === 0) return;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setHighlightedIndex((prev) => (prev < options.length - 1 ? prev + 1 : prev));
        break;
      case "ArrowUp":
        event.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case "Enter":
        event.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < options.length) {
          handleOptionSelect(options[highlightedIndex]);
        }
        break;
      case "Escape":
        setOpen(false);
        setHighlightedIndex(-1);
        inputRef.current?.blur();
        break;
      default:
        break;
    }
  };

  // Handle focus
  const handleFocus = () => {
    if (options.length > 0 && inputValue.length >= minSearchLength) {
      setOpen(true);
    }
  };

  // Handle blur
  const handleBlur = (event) => {
    // Delay closing to allow for option selection
    setTimeout(() => {
      setOpen(false);
      setHighlightedIndex(-1);
    }, 150);

    if (onBlur) {
      onBlur(event);
    }
  };

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Update input value when prop value changes
  useEffect(() => {
    if (value !== inputValue) {
      setInputValue(value);
    }
  }, [value, inputValue]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  return (
    <div ref={containerRef} className="relative w-full">
      <label htmlFor={props.id || name} className="block mb-2 text-gray-700">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      <div className="relative">
        <input
          ref={inputRef}
          name={name}
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          autoComplete="off"
          className={`w-full pr-10 px-4 py-2 rounded-md bg-[#EFF5F5]
            ${
              error
                ? "border border-red-500 text-red-600 focus:ring-red-400"
                : "border border-[#EDF1F2] text-[#303030] focus:ring-green-500"
            }
            focus:outline-none focus:ring-2 transition-colors`}
          {...props}
        />

        {/* Loading spinner or search icon */}
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          {loading ? (
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#75B83E]"></div>
          ) : (
            <svg
              className="h-5 w-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          )}
        </div>
      </div>

      {/* Dropdown */}
      {open && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {options.length > 0
            ? options.map((option, index) => (
                <div
                  key={option.id || index}
                  onClick={() => handleOptionSelect(option)}
                  className={`
                    px-3 py-2 cursor-pointer hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors
                    ${index === highlightedIndex ? "bg-blue-50" : ""}
                  `}
                >
                  <div className="text-sm font-medium text-gray-900 mb-1 line-clamp-2">
                    {option.address}
                  </div>
                  {option.type && (
                    <div className="text-xs text-gray-500 capitalize mb-1">
                      {option.type.replace("_", " ")}
                    </div>
                  )}
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>
                      {option.city && option.postcode
                        ? `${option.city}, ${option.postcode}`
                        : option.city || option.postcode || ""}
                    </span>
                    {option.country && (
                      <span className="uppercase text-xs font-medium">{option.country}</span>
                    )}
                  </div>
                </div>
              ))
            : !loading &&
              inputValue.length >= minSearchLength && (
                <div className="px-3 py-4 text-center text-sm text-gray-500 italic">
                  No addresses found for "{inputValue}"
                </div>
              )}
        </div>
      )}

      {/* Error message */}
      {error && helperText && <p className="mt-1 text-sm text-red-500">{helperText}</p>}
    </div>
  );
};

export default AddressAutocomplete;
